import React, { useRef } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import { Briefcase, GraduationCap, Award, BadgeCheck } from 'lucide-react';
import { profile } from '../data/profile';
import { fadeUp, chipPop, staggerContainer, EASE_OUT } from '../lib/motion';
import TextReveal from './TextReveal';
import './Experience.css';

const asidePanel = {
  hidden: { opacity: 0, x: 24, rotate: 1 },
  show: { opacity: 1, x: 0, rotate: 0, transition: { duration: 0.6, ease: EASE_OUT } },
};

const Experience = () => {
  const timelineRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: timelineRef,
    offset: ['start 0.75', 'end 0.6'],
  });
  const drawn = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  return (
    <section className="section experience" id="experience">
      <div className="container">
        <div className="section-head">
          <motion.span
            className="eyebrow"
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: '-60px' }}
          >
            Experience
          </motion.span>
          <TextReveal as="h2" className="section-title" text="Where I've worked" mode="words" inView />
        </div>

        <div className="experience__grid">
          <div className="experience__timeline" ref={timelineRef}>
            <motion.div
              className="experience__line"
              style={{ scaleY: drawn }}
              aria-hidden="true"
            />
            {profile.experience.map((job, i) => (
              <motion.div
                key={job.company + job.role}
                className="experience__item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-60px' }}
                transition={{ duration: 0.5, delay: (i % 3) * 0.06 }}
              >
                <motion.div
                  className="experience__dot"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: [0, 1.25, 1] }}
                  viewport={{ once: true, margin: '-60px' }}
                  transition={{ duration: 0.5, times: [0, 0.7, 1], ease: 'easeOut' }}
                >
                  <Briefcase size={13} />
                </motion.div>
                <div className="experience__content">
                  <div className="experience__top">
                    <h3 className="experience__role">{job.role}</h3>
                    <span className="experience__period">{job.period}</span>
                  </div>
                  <div className="experience__company">{job.company}</div>
                  <p className="experience__desc">{job.description}</p>
                  <motion.div
                    className="experience__stack"
                    variants={staggerContainer(0.04)}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: '-40px' }}
                  >
                    {job.stack.map((s) => (
                      <motion.span className="chip" key={s} variants={chipPop}>{s}</motion.span>
                    ))}
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>

          <aside className="experience__aside">
            <motion.div
              className="experience__panel glass gradient-border"
              variants={asidePanel}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
            >
              <h4 className="experience__panel-title"><GraduationCap size={16} /> Education</h4>
              {profile.education.map((ed) => (
                <div className="experience__edu" key={ed.school}>
                  <div className="experience__edu-school">{ed.school}</div>
                  <div className="experience__edu-degree">{ed.degree}</div>
                  <div className="experience__edu-period">{ed.period}</div>
                  {ed.verifyUrl && (
                    <a
                      className="experience__edu-verify"
                      href={ed.verifyUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <BadgeCheck size={13} /> Digital verification
                    </a>
                  )}
                </div>
              ))}
            </motion.div>

            <motion.div
              className="experience__panel glass gradient-border"
              variants={asidePanel}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: '-60px' }}
              transition={{ delay: 0.1 }}
            >
              <h4 className="experience__panel-title"><Award size={16} /> Certifications</h4>
              <ul className="experience__certs">
                {profile.certifications.map((c) => (
                  <li key={c}>{c}</li>
                ))}
              </ul>
            </motion.div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Experience;
